import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useCampfireLazyQuery } from '../../../global/network/useCampfireLazyQuery';
import { VolunteerProfileActions } from './volunteer-profile-actions';
import {
  GET_VOLUNTEER_COMMON_PROFILE,
  GET_VOLUNTEER_PROFILE_ENROLMENTS,
  GET_VOLUNTEER_PROFILE_INCOMPLETE_TASKS,
} from './volunteer-profile-model.gql';
import {
  GetVolunteerCommonProfile,
  GetVolunteerCommonProfileVariables,
  GetVolunteerCommonProfile_vm_volunteer as VolunteerType,
  GetVolunteerCommonProfile_vm_profileTask as ProfileTaskType,
} from './__generated__/GetVolunteerCommonProfile';
import {
  GetVolunteerProfileEnrolments,
  GetVolunteerProfileEnrolmentsVariables,
} from './__generated__/GetVolunteerProfileEnrolments';
import {
  GetVolunteerProfileIncompleteTasks,
  GetVolunteerProfileIncompleteTasksVariables,
} from './__generated__/GetVolunteerProfileIncompleteTasks';
import { VolunteerCommonActivityRole } from './__generated__/VolunteerCommonActivityRole';

interface VolunteerNameOptions {
  fullName: string;
  preferredName: string;
}

interface VolunteerCommonProfileContextInterface {
  profileId?: string;
  setProfileId: (x: string) => void;
  userId?: string;
  setUserId: (x: string) => void;
  volunteerNameOptions: VolunteerNameOptions;
  setVolunteerNameOptions: (x: VolunteerNameOptions) => void;
  unsavedComments: boolean;
  setUnsavedComments: (x: boolean) => void;
  showUnsavedCommentsDialog: boolean;
  setShowUnsavedCommentsDialog: (x: boolean) => void;
  availableRoles: VolunteerCommonActivityRole[];
  setAvailableRoles: (x: VolunteerCommonActivityRole[]) => void;
  updateParentVolunteerActions?: VolunteerProfileActions;
  setUpdateParentVolunteerActions: (x: VolunteerProfileActions) => void;
  volunteer?: VolunteerType;
  isHistoricalActivityEnrolment: boolean;
  setIsHistoricalActivityEnrolment: (x: boolean) => void;
  reloadNewData: () => void;
  error: any;
  profileTask?: ProfileTaskType;
  completedTasks: any[];
  completedRemovedTasks: any[];
  hasMore: boolean;
  loadMore: () => void;
  loading: boolean;
  activityEnrolments: any[];
  enrolmentsLoading: boolean;
  loadActivityEnrolments: () => void;
  incompleteTasks: any[];
  incompleteTasksLoading: boolean;
  loadIncompleteTasks: () => void;
}

interface VolunteerCommonProfileProviderProps {
  children: React.ReactNode;
  profileId?: string;
  userId?: string;
}

const VolunteerCommonProfileContext = React.createContext<VolunteerCommonProfileContextInterface | undefined>(
  undefined
);

const VolunteerCommonProfileProvider = (props: VolunteerCommonProfileProviderProps) => {
  const [profileId, setProfileId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [volunteerNameOptions, setVolunteerNameOptions] = useState<VolunteerNameOptions>({
    fullName: '',
    preferredName: '',
  });
  const [unsavedComments, setUnsavedComments] = useState<boolean>(false);
  const [showUnsavedCommentsDialog, setShowUnsavedCommentsDialog] = useState<boolean>(false);
  const [isHistoricalActivityEnrolment, setIsHistoricalActivityEnrolment] = useState<boolean>(false);
  const [availableRoles, setAvailableRoles] = useState<VolunteerCommonActivityRole[]>([]);
  const [updateParentVolunteerActions, setUpdateParentVolunteerActions] = useState<VolunteerProfileActions>();
  const [volunteer, setVolunteer] = useState<VolunteerType>();
  const [profileTask, setProfileTask] = React.useState<ProfileTaskType>();
  const [completedTasks, setCompletedTasks] = React.useState<any[]>([]);
  const [completedRemovedTasks, setCompletedRemovedTasks] = React.useState<any[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [offset, setOffset] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [activityEnrolments, setActivityEnrolments] = React.useState<any[]>([]);
  const [enrolmentsLoading, setEnrolmentsLoading] = React.useState(false);
  const [incompleteTasks, setIncompleteTasks] = React.useState<any[]>([]);
  const [incompleteTasksLoading, setIncompleteTasksLoading] = React.useState(false);
  const enrolmentsRequestedFor = useRef<string | undefined>(undefined);
  const incompleteTasksRequestedFor = useRef<string | undefined>(undefined);

  const LIMIT = 45;

  // Rosters are only read against the next 8 occurrences of each activity, so bound the
  // window to upcoming dates rather than pulling every roster the activity has ever had.
  const rosterStartDate = DateTime.local().toISODate();
  const rosterEndDate = DateTime.local()
    .plus({ months: 6 })
    .toISODate();

  const [getVolunteerCommonProfile, { error }] = useCampfireLazyQuery<
    GetVolunteerCommonProfile,
    GetVolunteerCommonProfileVariables
  >(GET_VOLUNTEER_COMMON_PROFILE);

  useEffect(() => {
    setProfileId(props.profileId);
    setUserId(props.userId);
  }, [props.profileId, props.userId]);

  useEffect(() => {
    if (profileId && userId) {
      getVolunteerCommonProfile({
        variables: {
          profileId,
          offset: 0,
          limit: LIMIT, // Load completed tasks with volunteer data
        },
      }).then(({ data }) => {
        if (!data || !data.vm.volunteer) {
          return;
        }
        setVolunteerNameOptions({
          fullName: `${data.vm.volunteer.profile.preferredName} ${data.vm.volunteer.profile.lastName}`,
          preferredName: data.vm.volunteer.profile.preferredName || '',
        });
        setAvailableRoles(data.vm.activityRoles);
        setVolunteer(data.vm.volunteer);
        setProfileTask(data.vm.profileTask);

        // Set completed tasks from initial load
        const tasks = data.vm.volunteer.profile.completedTasks || [];
        setCompletedTasks(tasks);
        setCompletedRemovedTasks(data.vm.volunteer.profile.completedRemovedTasks || []);
        setHasMore(tasks.length === LIMIT);
        setOffset(LIMIT);
      });
    }
  }, [profileId]);

  const [getActivityEnrolments] = useCampfireLazyQuery<
    GetVolunteerProfileEnrolments,
    GetVolunteerProfileEnrolmentsVariables
  >(GET_VOLUNTEER_PROFILE_ENROLMENTS);

  const [getIncompleteTasks] = useCampfireLazyQuery<
    GetVolunteerProfileIncompleteTasks,
    GetVolunteerProfileIncompleteTasksVariables
  >(GET_VOLUNTEER_PROFILE_INCOMPLETE_TASKS);

  const fetchActivityEnrolments = useCallback(() => {
    if (!profileId) return;
    setEnrolmentsLoading(true);
    setActivityEnrolments([]);
    getActivityEnrolments({
      variables: {
        profileId,
        isHistorical: isHistoricalActivityEnrolment,
        rosterStartDate,
        rosterEndDate,
      },
    })
      .then(({ data }) => setActivityEnrolments(data?.vm.volunteer?.activityEnrolments ?? []))
      .finally(() => setEnrolmentsLoading(false));
  }, [profileId, isHistoricalActivityEnrolment, getActivityEnrolments]);

  const loadActivityEnrolments = useCallback(() => {
    if (!profileId || enrolmentsRequestedFor.current === profileId) return;
    enrolmentsRequestedFor.current = profileId;
    fetchActivityEnrolments();
  }, [profileId, fetchActivityEnrolments]);

  const fetchIncompleteTasks = useCallback(() => {
    if (!profileId) return;
    setIncompleteTasksLoading(true);
    setIncompleteTasks([]);
    getIncompleteTasks({ variables: { profileId } })
      .then(({ data }) => setIncompleteTasks(data?.vm.volunteer?.tasks ?? []))
      .finally(() => setIncompleteTasksLoading(false));
  }, [profileId, getIncompleteTasks]);

  const loadIncompleteTasks = useCallback(() => {
    if (!profileId || incompleteTasksRequestedFor.current === profileId) return;
    incompleteTasksRequestedFor.current = profileId;
    fetchIncompleteTasks();
  }, [profileId, fetchIncompleteTasks]);

  // Refetch the historical/current enrolment split, but only once the tab has been opened
  // for this profile.
  useEffect(() => {
    if (enrolmentsRequestedFor.current === profileId) fetchActivityEnrolments();
  }, [isHistoricalActivityEnrolment]);

  const reloadNewData = () => {
    if (userId && profileId) {
      getVolunteerCommonProfile({
        variables: {
          profileId,
          offset: 0,
          limit: LIMIT,
        },
      }).then(({ data }) => {
        if (!data || !data.vm.volunteer) {
          return;
        }
        setVolunteerNameOptions({
          fullName: `${data.vm.volunteer.profile.preferredName} ${data.vm.volunteer.profile.lastName}`,
          preferredName: data.vm.volunteer.profile.preferredName || '',
        });
        setVolunteer(data.vm.volunteer);
        setAvailableRoles(data.vm.activityRoles || []);

        // Refresh completed tasks
        const tasks = data.vm.volunteer.profile.completedTasks || [];
        setCompletedTasks(tasks);
        setCompletedRemovedTasks(data.vm.volunteer.profile.completedRemovedTasks || []);
        setHasMore(tasks.length === LIMIT);
        setOffset(LIMIT);
      });

      if (enrolmentsRequestedFor.current === profileId) fetchActivityEnrolments();
      if (incompleteTasksRequestedFor.current === profileId) fetchIncompleteTasks();
    }
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const { data } = await getVolunteerCommonProfile({
        variables: {
          profileId: profileId!,
          offset,
          limit: LIMIT,
        },
      });

      if (data?.vm.volunteer?.profile.completedTasks) {
        const newTasks = data.vm.volunteer.profile.completedTasks;
        setCompletedTasks((prev) => [...prev, ...newTasks]);
        setHasMore(newTasks.length === LIMIT);
        setOffset((prev) => prev + LIMIT);
      }
    } finally {
      setLoading(false);
    }
  };

  const value = {
    profileId,
    setProfileId,
    userId,
    setUserId,
    volunteerNameOptions,
    setVolunteerNameOptions,
    unsavedComments,
    setUnsavedComments,
    showUnsavedCommentsDialog,
    setShowUnsavedCommentsDialog,
    availableRoles,
    setAvailableRoles,
    updateParentVolunteerActions,
    setUpdateParentVolunteerActions,
    volunteer,
    isHistoricalActivityEnrolment,
    setIsHistoricalActivityEnrolment,
    reloadNewData,
    error,
    profileTask,
    completedTasks,
    completedRemovedTasks,
    hasMore,
    loadMore,
    loading,
    activityEnrolments,
    enrolmentsLoading,
    loadActivityEnrolments,
    incompleteTasks,
    incompleteTasksLoading,
    loadIncompleteTasks,
  };

  return <VolunteerCommonProfileContext.Provider value={value} {...props} />;
};

export const useVolunteerCommonProfileContext = () => {
  const value = useContext(VolunteerCommonProfileContext);

  if (!value) throw new Error('Gotta be inside of a VolunteerCommonProfileContext.Provider, homie');

  return value;
};

export { VolunteerCommonProfileProvider };
